import React, { FC, useContext, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { FieldGroup, Form, FormApiInterface } from 'react-uforms';
import { Col, Container, Row } from 'styled-bootstrap-grid';
import { registerLocale } from 'react-datepicker';
import { Question } from '@ch/common/types/data';
import { NotificationList } from '@ch/common/components/notification';
import { RoutePath } from '../const';
import { useQuiz } from './hooks/use-quiz';
import { QuizWrap } from './quiz.styled';
import { fieldGroupDefault } from './const';
import QuizLayout from './quiz-layout';
import QuizFieldGroupConfirm from './quiz-field-group-confirm';
import { QuizContext } from './quiz-context';
import QuizCaptcha from './quiz-captcha';
import IdleTimer from '../idle-timer';
import QuizNav from './quiz-nav';
import { FieldGroupsData } from './types';
import { useQuizSubmit } from './hooks/use-quiz-submit';
import { useBrowserInterceptor } from './hooks/use-browser-interceptor';
import { useData } from '../use-data';
import { useQuizNotification } from './hooks/use-quiz-notification';
import QuizQuestion from './quiz-question';
import { useQuizCaptcha } from './hooks/use-quiz-captcha';
import { adaptSpecificFlows } from './helpers';
import { useDropdownPreloadOptions } from './hooks/use-quiz-preload-options';
import { useTabInterceptor } from './hooks/use-tab-interceptor';
import { useLang } from '../use-lang';
import { AppContext } from '../app-context';
import { Text } from '@ch/common/components/text';
import { useAnalyticsData } from './hooks/use-update-analytics-data';
import { analyticsAttribute } from './const';

const QuizForm: FC<{ eventQuestion: Question; steps: FieldGroupsData }> = ({ eventQuestion, steps }) => {
  const { flow } = useParams();
  const { validation, resetUserQuestions, submitted, token, userQuestions, intakeId } = useContext(QuizContext);
  const [formFields, setFormFields] = useState([]);
  const [focusedElement, setFocusedElement] = useState(null as Element);
  const { dropdownData } = useContext(AppContext);
  const { onSubmit, onError } = useQuizSubmit();
  const { updateAnalyticsData } = useAnalyticsData();
  const {
    data: {
      preventReload,
      keywords: { browserClose, captchaInfo, captchaNote },
      questions,
    },
  } = useData();
  const { lang } = useLang();
  const [showPersonaQuestion, setShowPersonaQuestion] = useState(true);

  useEffect(() => {
    (async () => {
      if (lang && lang !== 'en') {
        const module = await import(`date-fns/locale/${lang}`);
        registerLocale(lang, module?.default);
      }
    })();
  }, [lang]);

  useTabInterceptor();
  useBrowserInterceptor(preventReload, _ => browserClose);

  const updateConfirmationEmail = (api: FormApiInterface) => {
    const isChecked = api.getValue(analyticsAttribute.CONFIRMATION_EMAIL_CHECKBOX).length;
    if (isChecked) {
      const reporterEmailAddress = api.getValue('reporter_email');
      api.setValue('confirmation_email', reporterEmailAddress);
      document.getElementsByName('confirmation_email')[0].focus();
      document.getElementsByName(analyticsAttribute.CONFIRMATION_EMAIL_CHECKBOX)[0].focus();
    }
  };

  useEffect(() => {
    if (focusedElement) {
      if (focusedElement.getAttribute('name') && focusedElement.getAttribute('name') !== analyticsAttribute.PERSONA) {
        updateAnalyticsData({ abandonedField: focusedElement.getAttribute('name') }, intakeId);
      } else if (
        focusedElement.getAttribute('id') &&
        focusedElement.getAttribute('id').includes(analyticsAttribute.FOCUS_DROPDOWN)
      ) {
        updateAnalyticsData({ abandonedField: focusedElement.getAttribute('id').split('-')[2] }, intakeId);
      }
    }
  }, [focusedElement, intakeId, updateAnalyticsData]);

  useEffect(() => {
    if (submitted) {
      window.scrollTo(0, 0);
    }
  }, [submitted]);

  useEffect(() => {
    const handleFocus = event => {
      const currentFocusedElement =
        (document.hasFocus() &&
          document.activeElement !== document.body &&
          document.activeElement !== document.documentElement &&
          document.activeElement) ||
        event.target ||
        event.composedPath()[0] ||
        null;
      setFocusedElement(currentFocusedElement);
    };
    document.addEventListener('focusin', handleFocus);
    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);

  const formProps = {
    onSubmit,
    onError,
    defaultValues: {
      [eventQuestion.identifier]: flow,
    },
    validation: () => validation,
    onChange: (api: FormApiInterface, changedField: string) => {
      if (intakeId) {
        if (changedField === analyticsAttribute.PERSONA) {
          setShowPersonaQuestion(false);
          updateAnalyticsData({ persona: api.getValue(changedField), abandonedField: changedField }, intakeId);
        } else if (changedField === analyticsAttribute.CONFIRMATION_EMAIL_CHECKBOX) {
          updateConfirmationEmail(api);
        } else {
          if (formFields.find(field => field === changedField) === undefined) {
            setFormFields(formFields => {
              formFields.push(changedField);
              return formFields;
            });
          } else {
            const question = questions.find(
              question => question && question.identifier && question.identifier === changedField,
            );
            if (
              question &&
              navigator.appVersion.toLowerCase().includes('safari') &&
              !navigator.appVersion.toLowerCase().includes('chrome')
            ) {
              if (
                (question.field_type && (question.field_type === 'checkbox' || question.field_type === 'radio')) ||
                question.field_type === 'radio_vertical'
              ) {
                updateAnalyticsData({ abandonedField: changedField }, intakeId);
              }
            }
            if (
              question &&
              question.field_type &&
              question.field_type === 'dropdown' &&
              changedField === analyticsAttribute.REPORTER_COUNTRY
            ) {
              updateAnalyticsData({ country: api.getValue(changedField), abandonedField: changedField }, intakeId);
            }
          }
        }
      }
      resetUserQuestions(api);
      adaptSpecificFlows({ changedField, api, dropdownData, userQuestions });
    },
  };

  return (
    <Form {...formProps}>
      <QuizWrap>
        <QuizNav />
        <Container>
          <Row justifyContent="center">
            <Col xs={12} sm={10}>
              {submitted ? (
                <>
                  {Object.entries(steps).map(
                    ([name, { customLayout = false }]) =>
                      !customLayout && (
                        <FieldGroup key={name} {...{ name }}>
                          <div />
                        </FieldGroup>
                      ),
                  )}
                  <QuizFieldGroupConfirm />
                </>
              ) : (
                <>
                  {Object.entries(steps).map(
                    ([name, { questions = [], fullWidth = false, customLayout = false }]) =>
                      !customLayout && (
                        <FieldGroup
                          key={name}
                          {...{ name, ...(name === fieldGroupDefault && { defaultActive: true }) }}
                        >
                          <QuizLayout {...{ fullWidth }}>
                            {showPersonaQuestion && (
                              <Text
                                as="p"
                                $m={[0, 0, 30, 0]}
                                $color="primary"
                                $size="xxl"
                                $fontFamily="aeonikLight"
                                $align="center"
                              >
                                {steps?.type?.questions[0]?.question}
                              </Text>
                            )}
                            {!token && (
                              <>
                                <Text
                                  as="p"
                                  $m={[0, 0, 15, 0]}
                                  $color="primary"
                                  $size="md"
                                  $fontFamily="aeonikBold"
                                  $align="center"
                                >
                                  {captchaInfo}
                                </Text>
                                <QuizCaptcha />
                                <Text as="p" $m={[0, 0, 30, 0]} $color="primary" $size="sm" $align="center">
                                  {captchaNote}
                                </Text>
                              </>
                            )}
                            {questions
                              .filter(q => !q.duplicatorGroup)
                              .map(question => {
                                return (
                                  <QuizQuestion
                                    key={question.id}
                                    fullWidth={fullWidth}
                                    question={question}
                                    list={questions}
                                    customLayout={customLayout}
                                  />
                                );
                              })}
                          </QuizLayout>
                        </FieldGroup>
                      ),
                  )}
                  <IdleTimer />
                </>
              )}
            </Col>
          </Row>
        </Container>
      </QuizWrap>
    </Form>
  );
};

const Quiz: FC = () => {
  const { flow } = useParams();
  const {
    pregnancyValues,
    setPregnancyValues,
    documents,
    setDocuments,
    validation,
    setValidation,
    userQuestions,
    resetUserQuestions,
    steps,
    eventQuestion,
    submitted,
    setSubmitted,
    pregnancyFile,
    setPregnancyFile,
  } = useQuiz();

  const { setQuestion } = useData();

  const { token, intakeId, refreshToken, recaptchaSiteKey } = useQuizCaptcha();
  const { items: notifications, addNotification } = useQuizNotification();

  useDropdownPreloadOptions();

  if (!eventQuestion.options.find(el => el.identifier === flow)) {
    return <Navigate to={RoutePath.HOME} />;
  }

  return (
    <QuizContext.Provider
      value={{
        pregnancyValues,
        setPregnancyValues,
        documents,
        setDocuments,
        validation,
        setValidation,
        userQuestions,
        resetUserQuestions,
        submitted,
        setSubmitted,
        addNotification,
        setQuestion,
        intakeId,
        token,
        refreshToken,
        pregnancyFile,
        setPregnancyFile,
        recaptchaSiteKey,
      }}
    >
      <QuizForm {...{ eventQuestion, steps }} />
      <NotificationList items={notifications} />
    </QuizContext.Provider>
  );
};

export default Quiz;
